import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';

// กลุ่มอาการ — ใช้จัดหมวดสำหรับรายงานและการเฝ้าระวังการระบาด
const GROUPS = [
  { value: 'fever', label: 'ไข้' },
  { value: 'respiratory', label: 'ระบบทางเดินหายใจ' },
  { value: 'gi', label: 'ระบบทางเดินอาหาร' },
  { value: 'injury', label: 'อุบัติเหตุ/บาดเจ็บ' },
  { value: 'skin', label: 'ผิวหนัง/ตา' },
  { value: 'dental', label: 'ช่องปาก/ฟัน' },
  { value: 'mental', label: 'สุขภาพจิต' },
  { value: 'other', label: 'อื่นๆ' },
];

const groupLabel = (v) => GROUPS.find(g => g.value === v)?.label || v;

const emptyForm = () => ({ ns_id: null, name: '', group: 'other', watch: false, order: 0, active: true });

export default function NurseSymptoms() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(() => {
    axiosInstance.get('/api/nurse/symptoms?all=1')
      .then(res => setRows(res.data || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const edit = (row) => setForm({
    ns_id: row.ns_id,
    name: row.ns_name,
    group: row.ns_group,
    watch: !!row.ns_watch,
    order: row.ns_order,
    active: !!row.ns_active,
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      group: form.group,
      watch: form.watch ? 1 : 0,
      order: Number(form.order) || 0,
      active: form.active ? 1 : 0,
    };
    const req = form.ns_id
      ? axiosInstance.put(`/api/nurse/symptoms/${form.ns_id}`, payload)
      : axiosInstance.post('/api/nurse/symptoms', payload);
    req.then(() => {
      setForm(emptyForm());
      fetchRows();
      Swal.fire({ icon: 'success', title: 'บันทึกแล้ว', timer: 1200, showConfirmButton: false });
    })
      .catch(err => Swal.fire({ icon: 'error', title: 'ไม่สามารถบันทึกได้', text: err?.response?.data?.message || '' }))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="mb-3">
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item">ห้องพยาบาล</li>
            <li className="breadcrumb-item active">ตั้งค่ารายการอาการ</li>
          </ol>
          <h4 className="main-title mb-0">รายการอาการ</h4>
          <div className="fs-xs text-secondary">
            อาการที่ติ๊ก “เฝ้าระวัง” จะถูกนับรวมเพื่อดูการระบาดในห้องเรียนและหอพัก
          </div>
        </div>

        <Row className="g-3">
          <Col xl={4}>
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">{form.ns_id ? 'แก้ไขอาการ' : 'เพิ่มอาการ'}</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={submit}>
                  <Form.Group className="mb-2">
                    <Form.Label className="fw-semibold fs-xs mb-1">ชื่ออาการ</Form.Label>
                    <Form.Control value={form.name} maxLength={100} required
                      onChange={e => setForm({ ...form, name: e.target.value })} />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="fw-semibold fs-xs mb-1">กลุ่ม</Form.Label>
                    <Form.Select value={form.group} onChange={e => setForm({ ...form, group: e.target.value })}>
                      {GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="fw-semibold fs-xs mb-1">ลำดับการแสดง</Form.Label>
                    <Form.Control type="number" value={form.order}
                      onChange={e => setForm({ ...form, order: e.target.value })} />
                  </Form.Group>
                  <Form.Check type="switch" id="sym-watch" className="mb-1"
                    label="เฝ้าระวังการระบาด"
                    checked={form.watch} onChange={e => setForm({ ...form, watch: e.target.checked })} />
                  <Form.Check type="switch" id="sym-active" className="mb-3"
                    label="ใช้งาน (แสดงในรายการให้เลือก)"
                    checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
                  <div className="d-grid gap-2">
                    <Button type="submit" disabled={saving}>บันทึก</Button>
                    {form.ns_id && (
                      <Button variant="outline-secondary" onClick={() => setForm(emptyForm())}>เพิ่มรายการใหม่แทน</Button>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={8}>
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">ทั้งหมด {rows.length} รายการ</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover className="mb-0 fs-sm">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>ลำดับ</th>
                      <th>ชื่ออาการ</th>
                      <th>กลุ่ม</th>
                      <th>สถานะ</th>
                      <th style={{ width: 70 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.ns_id} className={r.ns_active ? '' : 'text-secondary'}>
                        <td>{r.ns_order}</td>
                        <td className="fw-semibold">{r.ns_name}</td>
                        <td>{groupLabel(r.ns_group)}</td>
                        <td>
                          {r.ns_watch ? <Badge bg="danger" className="me-1 fw-normal">เฝ้าระวัง</Badge> : null}
                          {r.ns_active ? null : <Badge bg="secondary" className="fw-normal">ปิดใช้งาน</Badge>}
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" onClick={() => edit(r)} aria-label="แก้ไข">
                            <i className="ri-edit-line"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </>
  );
}
